// src/pages/GladiatorsTV.jsx
// TV on button → turn-on animation → logo screen; then pick channel for content.
import React, { useEffect, useState, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DEFAULT_TEAM_STATS_TEAM_ID,
  isKnownTeamStatsTeamId,
  normalizeTeamStatsTeamId,
  TEAM_STATS_TEAMS,
} from '../utils/teamStatsConfig';
import LiveScoreboard from './LiveScoreboard';
import gladiatorsHero from '../assets/images/gladiators-hero.png';
import bradMelissaPhoto from '../assets/images/Brad-Melissa-Lenz.jpeg';
import gregPhoto from '../assets/images/Greg.jpeg';
import './GladiatorsTV.css';

const SIDELINE_HD_LIVE_URL = 'https://sidelinehd.com/live/GlassCityGladiators/';
const TV_POWER_ON_MS = 1800;
/** Session-only override so viewers can match the stat tracker age group without waiting for Admin settings. */
const TV_SCOREBOARD_TEAM_SESSION_KEY = 'gcg-gladiators-tv-scoreboard-team';

function extractYoutubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

// View-only YouTube embed: no control bar (play/pause), no keyboard, no fullscreen.
// URL is set from Firebase only—viewers cannot change what stream plays.
const YOUTUBE_EMBED_PARAMS = 'autoplay=0&rel=0&controls=0&disablekb=1&fs=0&modestbranding=1';

function buildEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  const ytId = extractYoutubeId(u);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?${YOUTUBE_EMBED_PARAMS}`;
  if (/^https?:\/\//.test(u)) return u;
  return null;
}

export default function GladiatorsTV() {
  const [games, setGames] = useState([]);
  const [liveStreamUrl, setLiveStreamUrl] = useState('');
  /** Which `stats/{teamId}/games` the embedded Live scoreboard follows (set in Admin → Gladiators TV). */
  const [liveScoreboardTeamId, setLiveScoreboardTeamId] = useState(DEFAULT_TEAM_STATS_TEAM_ID);
  /** When set, overrides Firestore `liveScoreboardTeamId` for this browser tab only (sessionStorage). */
  const [scoreboardTeamSessionOverride, setScoreboardTeamSessionOverride] = useState(() => {
    try {
      if (typeof sessionStorage === 'undefined') return null;
      const raw = sessionStorage.getItem(TV_SCOREBOARD_TEAM_SESSION_KEY);
      return isKnownTeamStatsTeamId(raw) ? raw.trim() : null;
    } catch {
      return null;
    }
  });
  const effectiveScoreboardTeamId = scoreboardTeamSessionOverride ?? liveScoreboardTeamId;
  const [selectedChannel, setSelectedChannel] = useState(null); // 'live' | { id, label, youtubeUrl } | null
  const [tvState, setTvState] = useState('off'); // 'off' | 'powering' | 'logo' | 'on'
  const [remoteMinimized, setRemoteMinimized] = useState(true); // start minimized so screen is large
  const [screenExpanded, setScreenExpanded] = useState(false); // full-viewport video for mobile/desktop
  const powerOnTimerRef = useRef(null);

  useEffect(() => {
    const ref = doc(db, 'settings', 'gladiatorsTv');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (Array.isArray(data.games)) setGames(data.games);
        setLiveStreamUrl(typeof data.liveStreamUrl === 'string' ? data.liveStreamUrl : '');
        setLiveScoreboardTeamId(normalizeTeamStatsTeamId(data.liveScoreboardTeamId));
      } else {
        setGames([]);
        setLiveStreamUrl('');
        setLiveScoreboardTeamId(DEFAULT_TEAM_STATS_TEAM_ID);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    return () => {
      if (powerOnTimerRef.current) clearTimeout(powerOnTimerRef.current);
    };
  }, []);

  const persistScoreboardTeamOverride = (teamId) => {
    setScoreboardTeamSessionOverride(teamId);
    try {
      sessionStorage.setItem(TV_SCOREBOARD_TEAM_SESSION_KEY, teamId);
    } catch {
      /* ignore */
    }
  };

  const clearScoreboardTeamOverride = () => {
    setScoreboardTeamSessionOverride(null);
    try {
      sessionStorage.removeItem(TV_SCOREBOARD_TEAM_SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const handleTvOn = () => {
    if (tvState === 'off') {
      setTvState('powering');
      if (powerOnTimerRef.current) clearTimeout(powerOnTimerRef.current);
      powerOnTimerRef.current = setTimeout(() => {
        setTvState('logo');
        powerOnTimerRef.current = null;
      }, TV_POWER_ON_MS);
    }
  };

  const handleSelectChannel = (channel) => {
    if (tvState === 'powering' || tvState === 'off') return;
    setSelectedChannel(channel);
    setTvState('powering');
    if (powerOnTimerRef.current) clearTimeout(powerOnTimerRef.current);
    powerOnTimerRef.current = setTimeout(() => {
      setTvState('on');
      powerOnTimerRef.current = null;
    }, TV_POWER_ON_MS);
  };

  const embedUrl =
    selectedChannel === 'live'
      ? buildEmbedUrl(liveStreamUrl)
      : selectedChannel && selectedChannel !== 'live' && selectedChannel.youtubeUrl
        ? buildEmbedUrl(selectedChannel.youtubeUrl)
        : null;

  const showLogo = tvState === 'logo';
  const showStayTuned = tvState === 'on' && selectedChannel === 'live' && !embedUrl;
  const showPlaceholder = tvState === 'on' && selectedChannel !== 'live' && !embedUrl;
  const showOffScreen = tvState === 'off';
  const canExpand = tvState === 'on' && (embedUrl || showStayTuned || showLogo || showPlaceholder);

  return (
    <div className="gladiators-tv-page">
      <div className="gladiators-tv-hero">
        <div className="gladiators-tv-hero-inner">
          <h1 className="gladiators-tv-title">Welcome to Gladiators TV</h1>
          <p className="gladiators-tv-subtitle gladiators-tv-welcome-text">
            Watch live games, catch recorded games, and keep up with all the fun action of the Glass City Gladiators. Use the remote to pick a game and enjoy.
          </p>
        </div>
      </div>

      {/* Remote (left, collapsible) + TV screen (right) */}
      <div className={`gladiators-tv-main-row ${remoteMinimized ? 'gladiators-tv-remote-minimized' : ''} ${screenExpanded ? 'gladiators-tv-screen-expanded' : ''}`}>
        <div className="gladiators-tv-remote-section">
          {remoteMinimized ? (
            <button
              type="button"
              className="gladiators-tv-remote-toggle"
              onClick={() => setRemoteMinimized(false)}
              title="Show remote"
            >
              <span className="gladiators-tv-remote-toggle-text">Remote</span>
            </button>
          ) : (
            <div className="gladiators-tv-remote">
              <div className="gladiators-tv-remote-header">
                <span className="gladiators-tv-remote-label">GLADIATORS TV</span>
                <button
                  type="button"
                  className="gladiators-tv-remote-minimize"
                  onClick={() => setRemoteMinimized(true)}
                  title="Minimize remote"
                  aria-label="Minimize remote"
                >
                  −
                </button>
              </div>
              <button
                type="button"
                className={`gladiators-tv-remote-power ${tvState !== 'off' ? 'gladiators-tv-remote-power-on' : ''}`}
                onClick={handleTvOn}
              >
                TV on
              </button>
              <div className="gladiators-tv-remote-buttons">
                <button
                  type="button"
                  className={`gladiators-tv-remote-btn ${selectedChannel === 'live' ? 'gladiators-tv-remote-btn-active' : ''}`}
                  onClick={() => handleSelectChannel('live')}
                  disabled={tvState === 'off'}
                >
                  <span className="gladiators-tv-remote-btn-num">LIVE</span>
                  <span className="gladiators-tv-remote-btn-text">Live Broadcast</span>
                </button>
                {games.map((g) => {
                  const isActive = selectedChannel && selectedChannel !== 'live' && selectedChannel.id === g.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      className={`gladiators-tv-remote-btn ${isActive ? 'gladiators-tv-remote-btn-active' : ''}`}
                      onClick={() => handleSelectChannel(g)}
                      disabled={tvState === 'off'}
                    >
                      <span className="gladiators-tv-remote-btn-text">{g.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="gladiators-tv-remote-hint">
                {tvState === 'off' ? 'Press TV on' : 'Select channel'}
              </div>
            </div>
          )}
        </div>

        <div className="gladiators-tv-video-section">
          <div className="gladiators-tv-video-wrapper gladiators-tv-screen-wrap">
          {/* Expand / close for larger view (mobile + desktop) */}
          {canExpand && !screenExpanded && (
            <button
              type="button"
              className="gladiators-tv-expand-btn"
              onClick={() => setScreenExpanded(true)}
              title="Expand to full view"
              aria-label="Expand to full view"
            >
              ⛶
            </button>
          )}
          {screenExpanded && (
            <button
              type="button"
              className="gladiators-tv-close-expand-btn"
              onClick={() => setScreenExpanded(false)}
              title="Close full view"
              aria-label="Close full view"
            >
              ✕
            </button>
          )}
          {/* TV turn-on overlay */}
          {tvState === 'powering' && (
            <div className="gladiators-tv-power-on" aria-hidden>
              <div className="gladiators-tv-power-on-screen" />
              <div className="gladiators-tv-power-on-line" />
              <div className="gladiators-tv-power-on-glow" />
            </div>
          )}

          {/* Content: embed, logo, placeholder, or off */}
          {tvState === 'on' && embedUrl && (
            <iframe
              title={selectedChannel === 'live' ? 'Live stream' : selectedChannel?.label || 'Gladiators stream'}
              src={embedUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="gladiators-tv-iframe gladiators-tv-iframe-visible"
            />
          )}

          {showLogo && (
            <div className="gladiators-tv-logo-screen">
              <img src={gladiatorsHero} alt="" className="gladiators-tv-logo-img" />
              <h2 className="gladiators-tv-logo-title">Gladiators TV</h2>
              <p className="gladiators-tv-logo-sub">Select a channel on the remote</p>
            </div>
          )}

          {showOffScreen && (
            <div className="gladiators-tv-off-screen">
              <span className="gladiators-tv-off-dot" />
              <p className="gladiators-tv-off-text">Press <strong>TV on</strong> on the remote</p>
            </div>
          )}

          {showStayTuned && (
            <div className="gladiators-tv-stay-tuned">
              <div className="gladiators-tv-stay-tuned-circle-wrap">
                <div className="gladiators-tv-stay-tuned-ring" aria-hidden />
                <div className="gladiators-tv-stay-tuned-logo-wrap">
                  <img src={gladiatorsHero} alt="" className="gladiators-tv-stay-tuned-logo" />
                </div>
              </div>
              <h2 className="gladiators-tv-stay-tuned-title">Stay tuned for more Gladiators Action</h2>
            </div>
          )}

          {showPlaceholder && (
            <div className="gladiators-tv-placeholder">
              <div className="gladiators-tv-placeholder-icon">📺</div>
              <h2 className="gladiators-tv-placeholder-title">No video</h2>
              <p className="gladiators-tv-placeholder-text">
                Invalid or missing YouTube link for this game.
              </p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Live scoreboard – Gazette shows as popup over ended game */}
      <div className="gladiators-tv-scoreboard-section">
        <h2 className="gladiators-tv-scoreboard-heading">Live Scoreboard</h2>
        <p className="gladiators-tv-scoreboard-subtitle">
          Scores and stats update in real time. After the game, the Gladiator Gazette appears as a popup.
        </p>
        <div className="gladiators-tv-scoreboard-team-picker" role="group" aria-label="Which team live scoreboard to show">
          <span className="gladiators-tv-scoreboard-team-picker-label">Team</span>
          <div className="gladiators-tv-scoreboard-team-picker-btns">
            {TEAM_STATS_TEAMS.map(({ teamId, label }) => (
              <button
                key={teamId}
                type="button"
                className={`gladiators-tv-scoreboard-team-btn ${
                  effectiveScoreboardTeamId === teamId ? 'gladiators-tv-scoreboard-team-btn-active' : ''
                }`}
                onClick={() => persistScoreboardTeamOverride(teamId)}
              >
                {label}
              </button>
            ))}
          </div>
          {scoreboardTeamSessionOverride != null && (
            <button type="button" className="gladiators-tv-scoreboard-team-reset" onClick={clearScoreboardTeamOverride}>
              Use admin default
            </button>
          )}
        </div>
        <LiveScoreboard teamId={effectiveScoreboardTeamId} recapAsPopup enableFanComments />
      </div>

      {/* Broadcast Team */}
      <div className="gladiators-tv-broadcast-section">
        <h2 className="gladiators-tv-broadcast-heading">Meet the Gladiators TV Team</h2>
        <p className="gladiators-tv-broadcast-intro">
          Our Broadcast team brings every game to life for the Gladiator Families and we are grateful for them!
        </p>
        <div className="gladiators-tv-broadcast-grid">
          <div className="gladiators-tv-broadcast-card">
            <img src={bradMelissaPhoto} alt="Brad and Melissa Lenz" className="gladiators-tv-broadcast-photo" />
            <h3 className="gladiators-tv-broadcast-name">Brad &amp; Melissa Lenz</h3>
            <p className="gladiators-tv-broadcast-role">Broadcast Team</p>
          </div>
          <div className="gladiators-tv-broadcast-card">
            <img src={gregPhoto} alt="Greg Hayes" className="gladiators-tv-broadcast-photo gladiators-tv-broadcast-photo-greg" />
            <h3 className="gladiators-tv-broadcast-name">Greg Hayes</h3>
            <p className="gladiators-tv-broadcast-role">Score Keeper</p>
          </div>
        </div>
      </div>
    </div>
  );
}
